// Player settings for rocketry mode, persisted in their own localStorage key
// (separate from the career, so resetting a career keeps them).
//
// G.settings.get(key) / set(key, value) / all(). Every change emits a
// 'settings' event with { key, value, settings } so systems can adapt live.
//
// quality: 'high' | 'medium' | 'low'. Systems read G.settings.tier(), which
// returns the numbers for the current level, and shed their cost without
// changing what the scene is meant to look like.

const KEY = 'rocketry.settings.v1';

const DEFAULTS = {
  quality: 'high',
  // True until the player picks a quality themselves; while it holds, perf.js
  // may step the quality down to keep the frame rate up.
  qualityAuto: true,
  musicVolume: 0.55,
  // The corner button: the score off without touching its volume.
  musicOn: true,
  sfxVolume: 1,
  radioVolume: 1,
  cinematicCamera: true,
  cameraShake: true
};

// Per-tier budgets. Anything expensive enough to matter should take its
// number from here rather than inventing its own switch.
//
// Volumetric clouds (volclouds.js): volClouds turns them on; volScale is the
// march resolution divisor and volRays caps the rays per frame on large
// buffers; volSteps and volLightSteps are the primary and light-march steps;
// volDetail adds the erosion octave; volAlpha is the history blend (lower is
// steadier) and volJitter the length of the jitter sequence. Low keeps the
// 2D decks.
//
// The Moon's terrain (moonmaterial.js, moondem.js): moonCraters is how many
// procedural crater classes the shader walks per pixel (5 = down to 11 m, each
// step drops the smallest), moonDemLevel the finest LOLA level (7: 167 m posts).
//
// Whole-frame caps (budget.js), applied while rocketry is up and restored on
// exit: pixelRatio and msaa are ceilings on whatever the app's own graphics
// preset asked for, never raises, and fxaa allows the FXAA pass that some
// presets run after SMAA. High leaves the app's settings alone.
export const QUALITY_TIERS = {
  high: {
    earthTiles: 48,
    moonCraters: 5, moonDemLevel: 7,
    skySteps: 20, skyLimbSteps: 32, shadowMap: 2048, clouds: true, envSize: 64, smokePool: 2000, plumeSteps: 26,
    pixelRatio: Infinity, msaa: Infinity, fxaa: true,
    volClouds: true, volScale: 2, volRays: 560000, volSteps: 128, volLightSteps: 8, volDetail: true,
    volAlpha: 0.15, volJitter: 16
  },
  medium: {
    earthTiles: 32,
    moonCraters: 4, moonDemLevel: 7,
    skySteps: 14, skyLimbSteps: 22, shadowMap: 1536, clouds: true, envSize: 64, smokePool: 1400, plumeSteps: 18,
    pixelRatio: 1, msaa: 0, fxaa: false,
    // Half-resolution march like high, with a lower ray cap (the divisor
    // rises on big windows) and fewer steps; a third-resolution march read
    // as smeared blobs against high.
    volClouds: true, volScale: 2, volRays: 380000, volSteps: 96, volLightSteps: 5, volDetail: true,
    volAlpha: 0.16, volJitter: 8
  },
  low: {
    earthTiles: 24,
    moonCraters: 3, moonDemLevel: 6,
    skySteps: 10, skyLimbSteps: 14, shadowMap: 1024, clouds: true, envSize: 32, smokePool: 800, plumeSteps: 12,
    pixelRatio: 0.8, msaa: 0, fxaa: false,
    volClouds: false
  }
};

export function createSettings(G) {
  let values = { ...DEFAULTS };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) values = { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (_) { /* private mode or corrupt; keep defaults */ }
  if (!QUALITY_TIERS[values.quality]) values.quality = 'high';
  // A tier the frame-rate guard chose is not the player's: it used to be
  // able to pick low, which has no volumetric clouds, and that stuck across
  // sessions. Hand those saves back to high; a player's own choice is kept.
  if (values.qualityAuto && values.quality !== 'high') { values.quality = 'high'; save(); }

  function save() {
    try { localStorage.setItem(KEY, JSON.stringify(values)); } catch (_) { /* ignore */ }
  }

  const api = {
    get(key) { return values[key]; },
    all() { return { ...values }; },
    // A player's choice. Picking a quality hands it back from the automatic
    // step-down for good.
    set(key, value, { auto = false } = {}) {
      if (!(key in DEFAULTS)) return;
      if (key === 'quality' && !QUALITY_TIERS[value]) return;
      if (key === 'quality' && !auto && values.qualityAuto) { values.qualityAuto = false; save(); }
      if (values[key] === value) return;
      values[key] = value;
      save();
      G.events.emit('settings', { key, value, settings: api.all(), auto });
    },
    tier() { return QUALITY_TIERS[values.quality]; }
  };
  return api;
}
